import React, { useMemo } from "react";
import { AlertCircle, Paperclip, RefreshCw } from "lucide-react";
import type { AttachmentKind, ChatMessage } from "../../backend/api/chat";
import { useMessageSearch } from "../../backend/queries/chat";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { FilterField } from "../../components/patterns/filter-bar";
import { cn } from "../../lib/utils";
import { conversationStamp } from "./chatFormat";

type KindFilter = "any" | AttachmentKind;
type WhenFilter = "any" | "week" | "month";

const KINDS: { value: KindFilter; label: string }[] = [
  { value: "any", label: "Any message" },
  { value: "image", label: "With a photo" },
  { value: "video", label: "With a video" },
  { value: "document", label: "With a document" },
];

// Messages are kept for three months, so "any time" already means that.
const WHENS: { value: WhenFilter; label: string; days?: number }[] = [
  { value: "any", label: "Any time" },
  { value: "week", label: "Past week", days: 7 },
  { value: "month", label: "Past month", days: 31 },
];

/** Whether the text holds something the API can search for. */
const hasSearchableText = (text: string) => /[\p{L}\p{N}]/u.test(text);

export type MessageSearchState = {
  text: string;
  kind: KindFilter;
  when: WhenFilter;
};

export const emptySearch: MessageSearchState = {
  text: "",
  kind: "any",
  when: "any",
};

/** Whether a search is in effect, so the inbox shows results, not threads. */
export const isSearching = (search: MessageSearchState) =>
  hasSearchableText(search.text) || search.kind !== "any";

/** The kind and time filters, shown under the inbox's search field. */
export function MessageSearchFilters({
  search,
  onChange,
}: {
  search: MessageSearchState;
  onChange: (next: MessageSearchState) => void;
}) {
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(9rem,1fr))] gap-2">
      <FilterField label="Attachment">
        {(id) => (
          <Select
            value={search.kind}
            onValueChange={(value) =>
              onChange({ ...search, kind: value as KindFilter })
            }
          >
            <SelectTrigger id={id} className="h-auto min-h-touch text-xs md:min-h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {KINDS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </FilterField>
      <FilterField label="Sent">
        {(id) => (
          <Select
            value={search.when}
            onValueChange={(value) =>
              onChange({ ...search, when: value as WhenFilter })
            }
          >
            <SelectTrigger id={id} className="h-auto min-h-touch text-xs md:min-h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WHENS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </FilterField>
    </div>
  );
}

type MessageSearchResultsProps = {
  search: MessageSearchState;
  /** Names a result's conversation the way the inbox list does. */
  conversationTitle: (conversationId: string) => string;
  currentUserId: string | null | undefined;
  onOpen: (conversationId: string, messageId: string) => void;
};

/**
 * Search results across every conversation the caller is in (SCRUM-41).
 * Choosing one opens its thread scrolled to the message.
 */
export function MessageSearchResults({
  search,
  conversationTitle,
  currentUserId,
  onOpen,
}: MessageSearchResultsProps) {
  const text = useDebouncedValue(search.text.trim(), 300);
  // Fixed when the filter is chosen, so the query key does not change every
  // render and refetch forever.
  const from = useFromDate(search.when);
  const params = {
    q: hasSearchableText(text) ? text : undefined,
    has: search.kind === "any" ? undefined : search.kind,
    from,
  };
  const results = useMessageSearch(params, Boolean(params.q || params.has));
  const words = useMemo(() => searchWords(params.q ?? ""), [params.q]);

  if (results.isPending) {
    return (
      <div className="space-y-1 p-2" aria-busy="true">
        {[0, 1, 2].map((row) => (
          <div key={row} className="space-y-2 rounded-md p-3">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-3 w-4/5" />
          </div>
        ))}
      </div>
    );
  }

  if (results.isError) {
    return (
      <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
        <AlertCircle className="h-5 w-5 text-destructive" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">
          The search could not be run.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void results.refetch()}
        >
          <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
          Try again
        </Button>
      </div>
    );
  }

  const messages = results.data;
  if (!messages.length) {
    return (
      <p
        className="px-6 py-10 text-center text-sm text-muted-foreground"
        role="status"
      >
        No messages match. Messages older than three months are removed.
      </p>
    );
  }

  return (
    <div className="p-2">
      <p className="sr-only" role="status">
        {messages.length}
        {results.hasNextPage ? " or more" : ""} matching messages
      </p>
      <ul className="space-y-1">
        {messages.map((message) => (
          <li key={message.id}>
            <button
              type="button"
              onClick={() => onOpen(message.conversation_id, message.id)}
              className={cn(
                "flex w-full flex-col gap-0.5 rounded-md px-3 py-2 text-left transition-colors",
                "hover:bg-accent hover:text-accent-foreground",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              )}
            >
              <span className="flex items-center gap-2">
                <span className="truncate text-sm font-medium">
                  {conversationTitle(message.conversation_id)}
                </span>
                <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                  {conversationStamp(message.created_at)}
                </span>
              </span>
              <ResultPreview
                message={message}
                mine={message.sender_id === currentUserId}
                words={words}
              />
            </button>
          </li>
        ))}
      </ul>
      {results.hasNextPage ? (
        <div className="flex justify-center pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void results.fetchNextPage()}
            disabled={results.isFetchingNextPage}
          >
            {results.isFetchingNextPage
              ? "Loading more"
              : results.isFetchNextPageError
                ? "Could not load more. Try again"
                : "Show more results"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function ResultPreview({
  message,
  mine,
  words,
}: {
  message: ChatMessage;
  mine: boolean;
  words: string[];
}) {
  const files = message.attachments.length;
  return (
    <span className="line-clamp-2 text-xs text-muted-foreground">
      {mine ? "You: " : ""}
      {message.body ? highlight(message.body, words) : null}
      {files ? (
        <span className="ml-1 inline-flex items-center gap-0.5 align-middle">
          <Paperclip className="h-3 w-3" aria-hidden="true" />
          {files === 1 ? "1 file" : `${files} files`}
        </span>
      ) : null}
    </span>
  );
}

/** The lowercased words a query matches, as prefixes, the way the API does. */
function searchWords(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
}

/** Marks every word in `body` that starts with one of the searched words. */
function highlight(body: string, words: string[]): React.ReactNode {
  if (!words.length) return body;
  return body.split(/([\p{L}\p{N}]+)/u).map((part, index) =>
    words.some((word) => part.toLowerCase().startsWith(word)) ? (
      <mark
        key={index}
        className="rounded-sm bg-primary/15 px-0.5 text-foreground"
      >
        {part}
      </mark>
    ) : (
      part
    ),
  );
}

/** The `from` bound for a time filter, fixed when the filter changes. */
function useFromDate(when: WhenFilter): string | undefined {
  return useMemo(() => {
    const days = WHENS.find((option) => option.value === when)?.days;
    return days
      ? new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString()
      : undefined;
  }, [when]);
}
